<template>
  <div style="display: flex; flex-direction: column; background: #F8F8F9;">
    <!-- Top toolbar matching Discharge Waterfall / Daily Operations -->
    <div class="cm-toolbar">
      <h2>Care Transitions Operations</h2>

      <!-- Date stepper -->
      <div class="cm-stepper">
        <button class="cm-icon-btn" @click="shiftRange(-1)"><FontAwesomeIcon :icon="['fas', 'chevron-left']" style="font-size: 10px;" /></button>
        <input class="cm-input cm-input-sm" type="date" v-model="dateFrom" @change="fetchSummary" style="width: 108px; text-align: center;" />
        <span style="color: #A5A5A6; font-size: 11px;">→</span>
        <input class="cm-input cm-input-sm" type="date" v-model="dateTo" @change="fetchSummary" style="width: 108px; text-align: center;" />
        <button class="cm-icon-btn" @click="shiftRange(1)" :disabled="isToday"><FontAwesomeIcon :icon="['fas', 'chevron-right']" style="font-size: 10px;" /></button>
      </div>

      <!-- Quick range presets -->
      <div class="cm-seg">
        <button
          v-for="preset in rangePresets"
          :key="preset.value"
          :class="{ on: activePreset === preset.value }"
          @click="applyPreset(preset.value)"
        >{{ preset.label }}</button>
      </div>

      <!-- View Selector (Arrivals / Oversight / Readmissions) -->
      <div class="cm-seg" style="margin-left: 12px;">
        <button
          v-for="t in tabs"
          :key="t.id"
          :class="{ on: activeTab === t.id }"
          @click="selectTab(t.id)"
        >{{ t.label }}</button>
      </div>

      <div style="margin-left: auto; display: flex; align-items: center; gap: 8px;">
        <button class="cm-icon-btn" @click="fetchSummary" :disabled="loading" title="Refresh">
          <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" :spin="loading" style="font-size: 11px;" />
        </button>
      </div>
    </div>

    <!-- Filter bar -->
    <div v-if="!loading" style="display: flex; align-items: center; gap: 8px; padding: 14px 16px 8px; flex-wrap: wrap;">
      <div style="min-width: 170px; max-width: 240px;">
        <SimpleMultiSelect
          :model-value="selectedMetricIds"
          :options="metricOptions"
          placeholder="All programs"
          @update:model-value="setPrograms"
        />
      </div>
      <div style="min-width: 170px; max-width: 240px;">
        <SimpleMultiSelect
          :model-value="managerId ? [managerId] : []"
          :options="managerOptions"
          :placeholder="options.can_view_all ? 'All managers' : 'My manager'"
          sensitive-labels
          @update:model-value="(val) => setManager(val && val.length ? val[val.length - 1] : null)"
        />
      </div>
      <div style="min-width: 170px; max-width: 240px;">
        <SimpleMultiSelect
          :model-value="selectedMemberIds"
          :options="memberOptions"
          placeholder="All staff"
          searchable
          sensitive-labels
          @update:model-value="setMembers"
        />
      </div>
      <button
        v-if="metricTouched || memberTouched || managerId"
        style="font-size: 11px; color: #6B7280; text-decoration: underline; background: none; border: none; cursor: pointer; padding: 4px 6px;"
        @click="resetFilters"
      >Reset filters</button>
    </div>

    <!-- Main Content Area -->
    <div style="padding: 10px 16px 20px; display: flex; flex-direction: column; gap: 14px;">
      <div v-if="error" class="cm-panel" style="padding: 12px 16px; color: #B42318; background: #FEF3F2; border-color: #FECDCA; font-size: 11.5px;">
        {{ error }}
      </div>

      <div v-if="loading" style="padding: 40px; text-align: center; color: #8B8B8D; font-size: 11.5px;">
        <div class="cm-spinner" style="margin: 0 auto 10px;"></div>
        Loading data...
      </div>

      <template v-else-if="summary && summary.rows && summary.rows.length">
        <!-- Scope note & Notices banner -->
        <div v-if="summary.notices && summary.notices.length" class="cm-panel" style="padding: 8px 12px; background: #FAFAFA;">
          <div v-for="(n, i) in summary.notices" :key="i" style="font-size: 10.5px; color: #8B8B8D; line-height: 1.4;">
            {{ n }}
          </div>
        </div>

        <!-- Main Summary Table Panel -->
        <div class="cm-panel">
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; border-bottom: 1px solid #E8E9E9;">
            <div style="font-size: 11.5px; font-weight: 600; color: #353537;">
              {{ tableTitle }}
            </div>
            <div style="display: flex; align-items: center; gap: 12px;">
              <span v-if="summary.scope && summary.scope.basis" style="font-size: 10.5px; color: #A5A5A6;">
                {{ summary.scope.basis }}
              </span>
              <ExportMenu
                :disabled="exporting || !summary.rows.length"
                :exports="[
                  { type: 'csv', action: () => exportSummary('csv') },
                  { type: 'excel', action: () => exportSummary('xlsx') },
                ]"
              />
            </div>
          </div>

          <div style="max-height: 480px; overflow: auto;">
            <table class="cm-table">
              <thead>
                <tr>
                  <th style="padding: 6px 10px; font-size: 10.5px; font-weight: 600; color: #6B7280; text-align: left; position: sticky; top: 0; background: #FFFFFF; z-index: 1;">
                    {{ activeTab === 'arrivals' ? 'Date' : 'Staff Member' }}
                  </th>
                  <th
                    v-for="col in summary.columns"
                    :key="col.key"
                    style="padding: 6px 10px; font-size: 10.5px; font-weight: 600; color: #6B7280; text-align: right; position: sticky; top: 0; background: #FFFFFF; z-index: 1;"
                    :title="col.definition"
                  >
                    {{ col.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in summary.rows" :key="row.id" style="border-top: 1px solid #F0F0F1;">
                  <td style="padding: 6px 10px; font-size: 11.5px; color: #353537; font-weight: 500;" :class="{ sensitive: activeTab !== 'arrivals' }">
                    {{ row.label }}
                  </td>
                  <td
                    v-for="col in summary.columns"
                    :key="col.key"
                    style="padding: 6px 10px; font-size: 11.5px; text-align: right; font-variant-numeric: tabular-nums;"
                  >
                    <template v-if="col.availability === 'unavailable'">
                      <span style="color: #A5A5A6;" :title="col.reason || 'Unavailable'">—</span>
                    </template>
                    <template v-else-if="cellOf(row, col).drillable">
                      <button
                        type="button"
                        style="background: none; border: none; padding: 0; color: #0284C7; font-weight: 600; cursor: pointer; text-decoration: underline; font-family: inherit; font-size: inherit;"
                        @click="openDrill(row, col)"
                      >
                        {{ formatCell(cellOf(row, col), col) }}
                      </button>
                    </template>
                    <template v-else>
                      <span style="color: #353537;">{{ formatCell(cellOf(row, col), col) }}</span>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Evidence Drilldown Panel -->
        <div v-if="drill.active" class="cm-panel" style="margin-top: 4px;">
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; border-bottom: 1px solid #E8E9E9;">
            <div>
              <span style="font-size: 12px; font-weight: 600; color: #353537;">
                {{ drill.title }}
              </span>
              <span v-if="!drill.loading" style="font-size: 11px; color: #6B7280; margin-left: 8px;">
                ({{ drill.total.toLocaleString() }} records)
              </span>
              <div v-if="drill.definition" style="font-size: 10.5px; color: #8B8B8D; margin-top: 2px;">
                {{ drill.definition }}
              </div>
            </div>
            <div style="display: flex; align-items: center; gap: 8px;">
              <ExportMenu
                :disabled="exporting || drill.loading || !drill.rows.length"
                :exports="[
                  { type: 'csv', action: () => exportDrill('csv') },
                  { type: 'excel', action: () => exportDrill('xlsx') },
                ]"
              />
              <button class="cm-icon-btn" @click="closeDrill" title="Close drilldown">
                <FontAwesomeIcon :icon="['fas', 'times']" style="font-size: 11px;" />
              </button>
            </div>
          </div>

          <div v-if="drill.loading" style="padding: 24px; text-align: center; color: #8B8B8D; font-size: 11.5px;">
            <div class="cm-spinner" style="margin: 0 auto 8px;"></div>
            Loading evidence rows...
          </div>
          <div v-else-if="!drill.rows.length" style="padding: 24px; text-align: center; color: #8B8B8D; font-size: 11.5px;">
            No evidence rows for this selection.
          </div>
          <div v-else style="max-height: 440px; overflow: auto;">
            <table class="cm-table">
              <thead>
                <tr>
                  <th
                    v-for="c in drill.columns"
                    :key="c.key"
                    style="padding: 6px 10px; font-size: 10.5px; font-weight: 600; color: #6B7280; position: sticky; top: 0; background: #FFFFFF; z-index: 1;"
                    :style="{ textAlign: c.format === 'number' ? 'right' : 'left' }"
                  >
                    {{ c.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(r, ri) in drill.rows"
                  :key="ri"
                  style="border-top: 1px solid #F0F0F1; cursor: pointer;"
                  @click="openPatient(r)"
                >
                  <td
                    v-for="c in drill.columns"
                    :key="c.key"
                    style="padding: 6px 10px; font-size: 11.5px; white-space: nowrap;"
                    :style="{ textAlign: c.format === 'number' ? 'right' : 'left' }"
                    :class="{ sensitive: c.sensitive }"
                  >
                    <span v-if="c.key === 'patient_name'" style="color: #0284C7; font-weight: 500;">
                      {{ r[c.key] || '—' }}
                    </span>
                    <span v-else-if="c.key === 'patient_identifier'" style="color: #6B7280;">
                      {{ r[c.key] || '—' }}
                    </span>
                    <span v-else style="color: #353537;">
                      {{ formatDrillVal(r[c.key], c) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>

      <div v-else style="padding: 40px; text-align: center; color: #A5A5A6; font-size: 11.5px;">
        No operational records found for this scope and date range.
      </div>
    </div>

    <!-- Patient Drawer for clicked row -->
    <div v-if="drawer.open" style="position: fixed; inset: 0; z-index: 60; display: flex; justify-content: flex-end; background: rgba(38,38,40,.28);"
         @click.self="closeDrawer">
      <div style="width: 60%; max-width: 1100px; background: #FFFFFF; height: 100%; overflow: auto; box-shadow: -4px 0 24px rgba(38,38,40,.18);">
        <Patient_Drawer
          :patient-identifier="drawer.patientIdentifier"
          :entity-id="drawer.entityId"
          :patient-name="drawer.patientName"
          :enabled-tabs="['details','programs','engagement','care_graph','utilization']"
          :show-header="true"
          :default-tab="drawer.defaultTab"
          metric-category="Care Management"
          @close="closeDrawer"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import abortable from '@/mixins/abortable';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const ENDPOINT = '/api/care-management/ct-operations/';

const easternToday = () => {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
};

const shiftDay = (dateStr, days) => {
  const d = new Date(`${dateStr}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

export default {
  name: 'CareManagement_CT_Operations',
  components: {
    SimpleMultiSelect,
    ExportMenu,
    Patient_Drawer,
    FontAwesomeIcon,
  },
  mixins: [abortable],
  data() {
    const today = easternToday();
    return {
      activeTab: 'arrivals',
      activePreset: 'today',
      dateFrom: today,
      dateTo: today,
      selectedMetricIds: [],
      selectedMemberIds: [],
      managerId: null,
      metricTouched: false,
      memberTouched: false,
      entityFilters: null,
      filterReset: 0,
      loading: false,
      error: '',
      summary: null,
      exporting: false,
      options: { metrics: [], members: [], managers: [], can_view_all: false },
      rangePresets: [
        { value: 'today', label: 'Today' },
        { value: 'lastweek', label: 'Last week' },
        { value: 'mtd', label: 'MTD' },
        { value: 'ytd', label: 'YTD' },
        { value: 'custom', label: 'Custom' },
      ],
      tabs: [
        { id: 'arrivals', label: 'Arrivals' },
        { id: 'oversight', label: 'Staff oversight' },
        { id: 'readmissions', label: 'Readmissions' },
      ],
      drill: {
        active: false,
        loading: false,
        rowId: null,
        measureKey: null,
        title: '',
        definition: '',
        rows: [],
        columns: [],
        total: 0,
      },
      drawer: {
        open: false,
        entityId: null,
        patientIdentifier: null,
        patientName: '',
        defaultTab: 'programs',
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['current_tenant_slug', 'isManagerViewer', 'isTenantAdmin', 'isSecurityUser']),
    isManagerLevel() {
      return !!(this.isManagerViewer || this.isTenantAdmin || this.isSecurityUser || this.current_user?.is_superuser);
    },
    contextKey() {
      return `${this.current_tenant_slug}:${this.current_user?.id || ''}:${this.isManagerLevel}`;
    },
    isToday() {
      return this.dateTo === easternToday();
    },
    metricOptions() {
      return (this.options.metrics || []).map((m) => ({ value: m.id, label: m.name }));
    },
    memberOptions() {
      return (this.options.members || []).map((m) => ({ value: m.id, label: m.name }));
    },
    managerOptions() {
      return (this.options.managers || []).map((m) => ({ value: m.id, label: m.name }));
    },
    tableTitle() {
      if (this.activeTab === 'arrivals') return 'Daily Opportunity Arrivals';
      if (this.activeTab === 'oversight') return 'Care Transitions Staff Oversight';
      return 'Readmission Review';
    },
    scopeParams() {
      return {
        date_from: this.dateFrom,
        date_to: this.dateTo,
        metric_ids: this.metricTouched ? [...this.selectedMetricIds] : null,
        member_ids: this.memberTouched ? [...this.selectedMemberIds] : null,
        manager_id: this.managerId,
        entity_filters: this.entityFilters,
      };
    },
  },
  watch: {
    contextKey: {
      immediate: true,
      handler() {
        this.fetchSummary();
      },
    },
  },
  methods: {
    selectTab(tabId) {
      if (this.activeTab === tabId) return;
      this.activeTab = tabId;
      this.closeDrill();
      this.fetchSummary();
    },
    shiftRange(dir) {
      this.activePreset = 'custom';
      this.dateFrom = shiftDay(this.dateFrom, dir);
      this.dateTo = shiftDay(this.dateTo, dir);
      this.fetchSummary();
    },
    applyPreset(key) {
      this.activePreset = key;
      if (key === 'custom') return;
      const today = easternToday();
      this.dateTo = today;
      if (key === 'today') this.dateFrom = today;
      if (key === 'mtd') this.dateFrom = `${today.slice(0, 7)}-01`;
      if (key === 'ytd') this.dateFrom = `${today.slice(0, 4)}-01-01`;
      if (key === 'lastweek') {
        const weekday = new Date(`${today}T12:00:00Z`).getUTCDay();
        this.dateTo = shiftDay(today, -((weekday + 6) % 7) - 1);
        this.dateFrom = shiftDay(this.dateTo, -6);
      }
      this.fetchSummary();
    },
    setPrograms(ids) {
      this.metricTouched = true;
      this.selectedMetricIds = ids;
      this.fetchSummary();
    },
    setMembers(ids) {
      this.memberTouched = true;
      this.selectedMemberIds = ids;
      this.fetchSummary();
    },
    setManager(id) {
      this.managerId = id;
      this.memberTouched = false;
      this.fetchSummary();
    },
    setEntityFilters(payload) {
      const next = payload?.entity_filters ?? null;
      if (JSON.stringify(next) === JSON.stringify(this.entityFilters)) return;
      this.entityFilters = next;
      this.fetchSummary();
    },
    resetFilters() {
      this.metricTouched = false;
      this.memberTouched = false;
      this.managerId = null;
      this.entityFilters = null;
      this.filterReset += 1;
      this.fetchSummary();
    },
    cellOf(row, col) {
      return (row.cells && row.cells[col.key]) || { value: null, drillable: false };
    },
    formatCell(cell, col) {
      if (cell == null || cell.value === null || cell.value === undefined) return '—';
      if (col.format === 'percent') {
        const pct = typeof cell.value === 'number' ? `${cell.value}%` : cell.value;
        if (cell.numerator != null && cell.denominator != null) {
          return `${pct} (${cell.numerator}/${cell.denominator})`;
        }
        return pct;
      }
      if (col.format === 'decimal') {
        return typeof cell.value === 'number' ? cell.value.toLocaleString(undefined, { maximumFractionDigits: 1 }) : cell.value;
      }
      if (typeof cell.value === 'number') return cell.value.toLocaleString();
      return cell.value;
    },
    formatDrillVal(val, col) {
      if (val === null || val === undefined || val === '') return '—';
      if (col.format === 'datetime' && val) {
        return new Date(val).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'short', timeStyle: 'short' });
      }
      return val;
    },
    async fetchSummary() {
      const signal = this._renewAbortSignal('summary');
      this.closeDrill();
      this.error = '';
      if (!this.current_tenant_slug || !this.current_user?.id) return;
      this.loading = true;
      try {
        const { data } = await this.$axios.post(ENDPOINT, {
          ...this.scopeParams,
          view: this.activeTab,
          action: 'summary',
        }, { signal });
        if (signal.aborted) return;
        this.summary = data;
        this.options = data.options || this.options;
        if (!this.metricTouched && data.options?.default_metric_ids) {
          this.selectedMetricIds = data.options.default_metric_ids;
        }
        if (!this.memberTouched && data.options?.members) {
          this.selectedMemberIds = data.options.members.map((m) => m.id);
        }
      } catch (e) {
        if (this._isAborted(e)) return;
        this.error = 'Could not load Care Transitions operations data. Please try again.';
      } finally {
        if (!signal.aborted) this.loading = false;
      }
    },
    async openDrill(row, col) {
      const signal = this._renewAbortSignal('drill');
      this.drill.active = true;
      this.drill.loading = true;
      this.drill.rowId = row.id;
      this.drill.measureKey = col.key;
      this.drill.title = `${row.label} · ${col.label}`;
      this.drill.definition = col.definition || '';
      this.drill.rows = [];
      this.drill.columns = [];
      this.drill.total = 0;
      try {
        const { data } = await this.$axios.post(ENDPOINT, {
          ...this.scopeParams,
          view: this.activeTab,
          action: 'drill',
          row_id: row.id,
          measure_key: col.key,
          limit: 100,
          offset: 0,
        }, { signal });
        if (signal.aborted) return;
        this.drill.rows = data?.rows || [];
        this.drill.columns = data?.columns || [];
        this.drill.total = data?.total || 0;
      } catch (e) {
        if (this._isAborted(e)) return;
      } finally {
        if (!signal.aborted) this.drill.loading = false;
      }
    },
    closeDrill() {
      this.drill.active = false;
      this.drill.rows = [];
    },
    openPatient(row) {
      if (!row || !row.entity_id) return;
      this.drawer = {
        open: true,
        entityId: row.entity_id,
        patientIdentifier: row.patient_identifier,
        patientName: row.patient_name || '',
        defaultTab: this.activeTab === 'readmissions' ? 'care_graph' : 'programs',
      };
    },
    closeDrawer() {
      this.drawer.open = false;
    },
    async exportSummary(format) {
      this.exporting = true;
      try {
        const { data, headers } = await this.$axios.post(ENDPOINT, {
          ...this.scopeParams,
          view: this.activeTab,
          action: 'export',
          export_target: 'summary',
          format,
        }, { responseType: 'blob' });
        const blob = new Blob([data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ct_${this.activeTab}_summary.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } finally {
        this.exporting = false;
      }
    },
    async exportDrill(format) {
      if (!this.drill.active) return;
      this.exporting = true;
      try {
        const { data } = await this.$axios.post(ENDPOINT, {
          ...this.scopeParams,
          view: this.activeTab,
          action: 'export',
          export_target: 'drill',
          row_id: this.drill.rowId,
          measure_key: this.drill.measureKey,
          format,
        }, { responseType: 'blob' });
        const blob = new Blob([data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ct_${this.activeTab}_drill.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
/* Zero custom font sizing or conflicting font families.
   Pure inheritance from design system tokens (.cm-panel, .cm-table, .cm-toolbar). */
</style>
